import { Chip, IconButton, Stack, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import VerifiedIcon from '@mui/icons-material/Verified'
import { tokens } from '../lib/tokens'
import type { ProfessionalInfo } from '../model/types'
import { DoctorAvatar } from './DoctorAvatar'

interface CollapsedBannerProps {
  info: ProfessionalInfo
  onExpand: () => void
}

export function CollapsedBanner({ info, onExpand }: CollapsedBannerProps) {
  const { fullName, title, speciality, hasTraining, trainingLabel } = info
  const subtitle = [title, speciality].filter(Boolean).join(' · ')

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: { xs: 1.5, md: 3 },
        py: 1,
        bgcolor: tokens.surfaceContainerLowest,
        borderRadius: 50,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        border: '1px solid',
        borderColor: 'rgba(25,28,30,0.05)',
        transition: 'border-color 0.3s',
        '&:hover': { borderColor: 'rgba(0,72,141,0.2)' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, md: 2 }}>
        <DoctorAvatar />

        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'baseline' }} spacing={{ xs: 0, md: 1.5 }}>
          <Typography fontFamily='"Manrope", sans-serif' fontWeight={700} fontSize="1rem" color={tokens.onSurface} letterSpacing="-0.01em" lineHeight={1.3}>
            {fullName}
          </Typography>
          {subtitle && (
            <Typography fontFamily='"Inter", sans-serif' fontSize={{ xs: '0.75rem', md: '0.8125rem' }} color={tokens.secondary} lineHeight={1.4}>
              {subtitle}
            </Typography>
          )}
        </Stack>

        {hasTraining && (
          <Chip
            icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
            label={trainingLabel}
            size="small"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              bgcolor: `${tokens.secondaryContainer}4D`,
              color: tokens.primary,
              fontSize: '0.625rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              '& .MuiChip-icon': { color: tokens.primary },
            }}
          />
        )}
      </Stack>

      <IconButton onClick={onExpand} sx={{ color: tokens.secondary, '&:hover': { bgcolor: tokens.primaryContainer, color: '#fff' } }}>
        <ExpandMoreIcon />
      </IconButton>
    </Stack>
  )
}
